import { AreaChart } from '@mantine/charts';
import {
	type BoxProps,
	SegmentedControl,
	type SegmentedControlItem,
	Skeleton,
} from '@mantine/core';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paper } from '../../../components/Paper';
import { queryWithFilter } from '../../../util/api/queryWithFilter';
import { formatToCurrency, formatToDateString } from '../../../util/formatting';
import type { FilterDto } from '../../../util/types/FilterDto';
import type { ApiResponse } from '../../../util/types/Response';
import type { DailySaleDto } from '../types/DailySaleDto';

interface SummaryProps extends BoxProps {
	filter: FilterDto;
}

const options: SegmentedControlItem[] = [
	{ value: 'none', label: 'None' },
	{ value: 'lastYear', label: 'Last Year' },
	{ value: 'afterVat', label: 'After VAT' },
];

export function SalesSummary({ filter, ...boxProps }: SummaryProps) {
	const { organisation } = useParams();

	const dailySalesQuery = queryWithFilter<DailySaleDto[]>(
		`${organisation}/dashboard/daily-sales`,
		filter,
	);

	const [displayOption, setDisplayOption] = useState(options[0].value);

	return (
		<Paper
			title={'SALES SUMMARY'}
			headerContent={
				<SegmentedControl
					value={displayOption}
					onChange={setDisplayOption}
					data={options}
					mb={'xs'}
				/>
			}
		>
			<Skeleton
				visible={dailySalesQuery.isLoading || !dailySalesQuery.data}
				h={boxProps.h}
			>
				<AreaChart
					{...boxProps}
					data={mapSalesToChartFormat(displayOption, dailySalesQuery.data)}
					dataKey={'date'}
					valueFormatter={(value) => formatToCurrency(value)}
					series={[
						{
							label: 'Net Amount',
							name: 'netAmount',
							color: 'medepos-secondary.0',
						},
						{
							label: 'After Vat Amount',
							name: 'afterVat',
							color: 'medepos-secondary.1',
						},
						{
							label: 'Last Year Net Amount',
							name: 'lastYear',
							color: 'medepos-secondary.2',
						},
					]}
					curveType={'natural'}
				/>
			</Skeleton>
		</Paper>
	);
}

export function mapSalesToChartFormat(
	displayOption: string,
	dailySales?: ApiResponse<DailySaleDto[]>,
): Record<string, any>[] {
	if (dailySales == null || dailySales.value == null) {
		return noDataToDisplay;
	}

	if (displayOption === options[0].value) {
		return dailySales.value.map((sale) => {
			return {
				date: formatToDateString(sale.date),
				netAmount: sale.netAmount,
			};
		});
	}

	if (displayOption === options[1].value) {
		return dailySales.value.map((sale) => {
			return {
				date: formatToDateString(sale.date),
				netAmount: sale.netAmount,
				afterVat: sale.netAmount - sale.vat,
				lastYear: sale.lastYearNetAmount,
			};
		});
	}

	if (displayOption === options[2].value) {
		return dailySales.value.map((sale) => {
			return {
				date: formatToDateString(sale.date),
				netAmount: sale.netAmount,
				lastYear: sale.lastYearNetAmount,
			};
		});
	}

	if (displayOption === options[3].value) {
		return dailySales.value.map((sale) => {
			return {
				date: formatToDateString(sale.date),
				netAmount: sale.netAmount,
				afterVat: sale.netAmount - sale.vat,
			};
		});
	}

	return dailySales.value.map((sale) => {
		return {
			date: formatToDateString(sale.date),
			netAmount: sale.netAmount,
			afterVat: sale.netAmount - sale.vat,
		};
	});
}

const noDataToDisplay = [
	{
		date: 'No Data',
		netAmount: 0,
	},
];
